import { defineStore } from "pinia";
import useWheelAnalysisQuery from "@/composables/useWheelAnalysisQuery";
import axios from "axios";

import { useAnalysisStore } from "@/stores/Setup/analysis";
import { useUserInfoStore } from "@/stores/Setup/userInfo";
import { useCriticalGoalsStore } from "@/stores/WheelAnalysis/criticalGoals";
import { useGoalAStore } from "@/stores/WheelAnalysis/goalA";
import { useAdditionalGoalsStore } from "@/stores/WheelAnalysis/additionalGoals";
import { useGoalBStore } from "@/stores/WheelAnalysis/goalB";
import { useAllGoalsStore } from "@/stores/WheelAnalysis/allGoals";
import { useKeywordStore } from "@/stores/WheelAnalysis/keyword";
import { useImageStore } from "@/stores/WheelAnalysis/image";
import { useGoal11TargetsStore } from "@/stores/WheelAnalysis/goal11Targets";

// Use the global i18n instance (Vue 2 + vue-i18n common pattern)
import {i18n} from "@/i18n";

export const useChallengeStore = defineStore("challenge", {
    state: () => ({
        challengeType: "",
        challengeTypeOther: "",

        // store the actual text (what user edits), but initialize from i18n
        challengeStatement: "",
        challengeStatementTouched: false, // prevents overwriting on locale changes

        name: "",
        editMode: true,
        isPublished: null,
    }),

    actions: {
        // Call this from the component on mount (and optionally when locale changes)
        initDefaults() {
            if (!this.challengeStatementTouched && (!this.challengeStatement || this.challengeStatement.trim() === "")) {
                // translated default template
                this.challengeStatement = i18n.t("challenge.defaultStatement");
            }
        },

        async fetchWheelAnalysis() {
            await useWheelAnalysisQuery();
        },

        nameChanged() {
            const analysisStore = useAnalysisStore();
            if (analysisStore.wheelAnalysisId !== null) {
                axios.put(
                    `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${analysisStore.wheelAnalysisId}/name`,
                    { name: this.name }
                );
            }
        },

        challengeTypeChanged() {
            const analysisStore = useAnalysisStore();
            if (analysisStore.wheelAnalysisId !== null) {
                axios.put(
                    `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${analysisStore.wheelAnalysisId}/challenge_type`,
                    { challenge_type: this.challengeType }
                );
            }
        },

        challengeTypeOtherChanged() {
            const analysisStore = useAnalysisStore();
            if (analysisStore.wheelAnalysisId !== null) {
                axios.put(
                    `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${analysisStore.wheelAnalysisId}/challenge_type_other`,
                    { challenge_type_other: this.challengeTypeOther }
                );
            }
        },

        // ✅ mark touched so we don't clobber with locale changes
        setChallengeStatement(value) {
            this.challengeStatement = value;
            this.challengeStatementTouched = true;
        },

        challengeStatementChanged() {
            const analysisStore = useAnalysisStore();
            if (analysisStore.wheelAnalysisId !== null && this.challengeStatement !== null) {
                axios.put(
                    `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${analysisStore.wheelAnalysisId}/challenge_statement`,
                    { challenge_statement: this.challengeStatement }
                );
            }
        },

        isPublishedChanged() {
            const analysisStore = useAnalysisStore();
            if (analysisStore.wheelAnalysisId !== null && this.isPublished !== null) {
                axios.put(
                    `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${analysisStore.wheelAnalysisId}/is_published`,
                    { is_published: this.isPublished }
                );
            }
        },

        exitedField() {
            const analysisStore = useAnalysisStore();
            const userInfoStore = useUserInfoStore();
            const criticalGoalsStore = useCriticalGoalsStore();
            const goalAStore = useGoalAStore();
            const goalBStore = useGoalBStore();
            const additionalGoalsStore = useAdditionalGoalsStore();
            const allGoalsStore = useAllGoalsStore();
            const keywordStore = useKeywordStore();
            const imageStore = useImageStore();
            const goal11TargetsStore = useGoal11TargetsStore();

            if (analysisStore.wheelAnalysisId === null && !analysisStore.creatingWheelAnalysis) {
                analysisStore.creatingWheelAnalysis = true;

                const hexCode = [...Array(10)]
                    .map(() => Math.floor(Math.random() * 16).toString(16))
                    .join("");

                const wheelAnalysisData = {
                    analyses_id: userInfoStore.userInfoId,
                    challenge_type: this.challengeType,
                    challenge_statement: this.challengeStatement,
                    goal_11_selected_targets: [goal11TargetsStore.selectedTargets].toString(),
                    selected_critical_goals: [criticalGoalsStore.selectedCriticalGoals].toString(),
                    goal_a_index: criticalGoalsStore.goalAIndex,
                    goal_b_index: criticalGoalsStore.goalBIndex,
                    goal_a_selected_targets: [goalAStore.selectedTargets].toString(),
                    goal_b_selected_targets: [goalBStore.selectedTargets].toString(),
                    additional_goals_selected_goals: [additionalGoalsStore.selectedGoals].toString(),
                    additional_goals_selected_targets: JSON.stringify(additionalGoalsStore.selectedTargets),
                    all_goals_selected_goals: JSON.stringify(allGoalsStore.selectedGoals),
                    all_goals_selected_targets: JSON.stringify(allGoalsStore.selectedTargets),
                    keyword1: keywordStore.keyword1,
                    keyword2: keywordStore.keyword2,
                    keyword3: keywordStore.keyword3,
                    challenge_type_other: this.challengeTypeOther,
                    notes: allGoalsStore.reviewNotes,
                    hex_code: hexCode,
                    image_file: imageStore.previewImage,
                    name: this.name,
                };

                analysisStore.wheelAnalysisHexCode = hexCode;

                axios
                    .post(`${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/create`, wheelAnalysisData)
                    .then((response) => {
                        analysisStore.wheelAnalysisId = response.data.id;
                        analysisStore.creatingWheelAnalysis = false;
                    })
                    .catch((error) => {
                        console.error("There was an error!", error);
                        analysisStore.creatingWheelAnalysis = false;
                    });
            }
        },
    },

    persist: true,
});
